import React, { useCallback } from 'react'
import { useIntl } from 'react-intl'

import ModalFooterActionContainer from '../TripModal/TripItems/Create/ModalFooterActionContainer'

import TextButton from 'LuxKit/Button/TextButton'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { useDeleteDocument } from 'tripPlanner/hooks/api/documents'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'

interface Props {
  tripId: string
  documentKey: string
}

function DeleteConfirmation({ tripId, documentKey }: Props) {
  const intl = useIntl()
  const isMobile = useScreenSizeOnly('mobile')

  const { closeModal } = useModalCallbacks()
  const modalElement = useModalElementContext()

  const close = useCallback(() => {
    closeModal()
    modalElement?.resolve()
  }, [closeModal, modalElement])

  const { mutate: deleteDocument } = useDeleteDocument({
    onSuccess: () => {
      showSnackbar(intl.formatMessage({
        id: 'fpJO/ndLse',
        defaultMessage: 'Document deleted successfully',
        description: 'Success notification after deleting a trip document',
      }), 'success')
    },
    onError: () => {
      showSnackbar(intl.formatMessage({
        id: '3BZC+QT0rM',
        defaultMessage: 'Failed to delete document',
        description: 'Error notification when trip document deletion fails',
      }), 'critical')
    },
    tripId,
  })

  const handleDelete = useCallback(() => {
    deleteDocument({ documentKey })
    close()
  }, [close, deleteDocument, documentKey])

  return (
    <>
      <ModalHeader title={intl.formatMessage({
        id: 'ZZW3jKN65W',
        defaultMessage: 'Are you sure you wish to delete the document?',
        description: 'Confirmation modal title when deleting a trip document',
      })}
      />
      <ModalBody>
        <ModalContent>
          <BodyTextBlock variant="large">
            {intl.formatMessage({
              id: 'NeH2r2+avW',
              defaultMessage: 'This will delete the document permanently. If you change your mind, you will need to upload the document again.',
              description: 'Warning message explaining that document deletion is permanent',
            })}
          </BodyTextBlock>
        </ModalContent>
      </ModalBody>
      <ModalFooter>
        <ModalFooterActionContainer>
          <TextButton
            kind="tertiary"
            size="large"
            fit={`${isMobile ? 'full-width' : 'auto'}`}
            onClick={close}
          >
            {intl.formatMessage({
              id: '6L4hVDEttH',
              defaultMessage: 'Cancel',
              description: 'Button to cancel document deletion and close the confirmation modal',
            })}
          </TextButton>
          <TextButton
            kind="primary"
            size="large"
            fit={`${isMobile ? 'full-width' : 'auto'}`}
            onClick={handleDelete}
            type="submit"
          >
            {intl.formatMessage({
              id: 'v1RcwoPYgX',
              defaultMessage: 'Yes, delete',
              description: 'Button to confirm permanent deletion of a trip document',
            })}
          </TextButton>
        </ModalFooterActionContainer>
      </ModalFooter>
    </>
  )
}

export default DeleteConfirmation
